import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { detectMobileOS } from '../../utils/userAgentUtils';
import { MobileOS } from '../../types/misc';
import analyticsService from '../../services/analyticsService';
import DownloadButton from '../../components/misc/DownloadButton';
import { setLanguage } from '../../redux/slices/applicationSlice';
import useLanguage from '../../hooks/useLanguage';
import { Language } from '../../types/requests';

interface ABTestVariant {
  id: string;
  styleId: string; // Which style variant to use (a, b, or c)
  headingKey: string;
  subheadingKey: string;
}

const abTestVariants: ABTestVariant[] = [
  {
    id: 'a',
    styleId: 'a',
    headingKey: 'page.download.variants.a.heading',
    subheadingKey: 'page.download.variants.a.subheading',
  },
  {
    id: 'b',
    styleId: 'b',
    headingKey: 'page.download.variants.b.heading',
    subheadingKey: 'page.download.variants.b.subheading',
  },
  {
    id: 'c',
    styleId: 'c',
    headingKey: 'page.download.variants.c.heading',
    subheadingKey: 'page.download.variants.c.subheading',
  },
  {
    id: 'd',
    styleId: 'c',
    headingKey: 'page.download.variants.d.heading',
    subheadingKey: 'page.download.variants.d.subheading',
  },
  {
    id: 'e',
    styleId: 'c',
    headingKey: 'page.download.variants.e.heading',
    subheadingKey: 'page.download.variants.e.subheading',
  },
  {
    id: 'f',
    styleId: 'c',
    headingKey: 'page.download.variants.f.heading',
    subheadingKey: 'page.download.variants.f.subheading',
  },
  {
    id: 'g',
    styleId: 'c',
    headingKey: 'page.download.variants.g.heading',
    subheadingKey: 'page.download.variants.g.subheading',
  },
  {
    id: 'h',
    styleId: 'c',
    headingKey: 'page.download.variants.h.heading',
    subheadingKey: 'page.download.variants.h.subheading',
  },
  {
    id: 'i',
    styleId: 'c',
    headingKey: 'page.download.variants.i.heading',
    subheadingKey: 'page.download.variants.i.subheading',
  },
];

function DownloadPage() {
  const [searchParams] = useSearchParams();
  const mobileOS = detectMobileOS();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lang = useLanguage();

  // Get variant from URL parameter, default to 'a'
  const variantId = searchParams.get('variant') || 'a';
  const variant = abTestVariants.find((v) => v.id === variantId) || abTestVariants[0];

  // Variant-specific presentation
  const stylesByVariant: Record<
    string,
    {
      backgroundColor: string;
      headingClass: string;
      subheadingClass: string;
      hearts: string[]; // classNames for positioned hearts
    }
  > = {
    a: {
      backgroundColor: '#F5A68A',
      headingClass: 'text-[#0F1531]',
      subheadingClass: 'text-[#182046]/90',
      hearts: [
        'pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rotate-[28deg] opacity-80 md:h-[22rem] md:w-[22rem]',
      ],
    },
    b: {
      backgroundColor: '#F3E2D8',
      headingClass: 'text-[#0F1531]',
      subheadingClass: 'text-[#0F1531]/80',
      hearts: [
        'pointer-events-none absolute -bottom-24 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 -rotate-6 opacity-90 md:h-[34rem] md:w-[34rem]',
      ],
    },
    c: {
      backgroundColor: '#9DAAF2',
      headingClass: 'text-[#0F1531]',
      subheadingClass: 'text-[#0F1531]/85',
      hearts: [
        'pointer-events-none absolute -left-24 bottom-9 h-72 w-72 rotate-[18deg] opacity-90 md:h-96 md:w-96',
        'pointer-events-none absolute -right-16 -bottom-24 h-64 w-64 -rotate-12 opacity-90 md:h-80 md:w-80',
      ],
    },
  };
  const variantStyle = stylesByVariant[variant.styleId] || stylesByVariant.a;

  // Get source parameter for tracking
  const source = searchParams.get('source') || 'direct';
  const campaign = searchParams.get('campaign') || 'unknown';

  useEffect(() => {
    // Detect mobile OS
    const detectedOS = detectMobileOS();

    // Track page visit
    analyticsService.trackPageView({
      variant: variant.id,
      source,
      campaign,
      platform: detectedOS,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      url: window.location.href,
    });
  }, [variant.id, source, campaign]);

  const handlePlayStoreClick = () => {
    // Track button click
    const platform = detectMobileOS();
    analyticsService.trackEvent('download_click', {
      platform,
      variant: variant.id,
      source,
      campaign,
    });
  };

  const handleAppStoreClick = () => {
    // Track button click
    const platform = detectMobileOS();
    analyticsService.trackEvent('download_click', {
      platform,
      variant: variant.id,
      source,
      campaign,
    });
  };

  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center px-4 py-8"
      style={{ backgroundColor: variantStyle.backgroundColor }}>
      {/* Language switcher */}
      <img
        src={`${process.env.PUBLIC_URL}/images/flags/${lang === Language.NL ? Language.EN : Language.NL}.svg`}
        alt={lang === Language.NL ? 'English' : 'Nederlands'}
        onClick={() => dispatch(setLanguage(lang === Language.NL ? Language.EN : Language.NL))}
        className="absolute right-4 top-4 z-[2] h-6 w-6 cursor-pointer"
      />
      {/* Decorative hearts per variant */}
      {variantStyle.hearts.map((cls, idx) => (
        <img key={idx} src="/images/figures/heart.svg" alt="" aria-hidden className={cls} />
      ))}

      <div className="relative z-[1] w-full max-w-lg space-y-8 text-center">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img src="/logo.png" alt={t('page.download.appLogoAlt')} className="h-20 w-auto md:h-24" />
        </div>

        {/* Heading */}
        <div className="space-y-4">
          <h1 className={twMerge('text-4xl font-extrabold leading-tight md:text-5xl', variantStyle.headingClass)}>
            {t(variant.headingKey)}
          </h1>
          <p className={twMerge('text-xl font-medium md:text-2xl', variantStyle.subheadingClass)}>
            {t(variant.subheadingKey)}
          </p>
        </div>

        {/* Download Buttons */}
        <div className="pt-8">
          <DownloadButton
            className="items-center justify-center"
            onClickPlayStore={handlePlayStoreClick}
            onClickAppStore={handleAppStoreClick}
          />
        </div>

        {/* Platform message */}
        <p className="mt-4 text-sm text-[#0F1531]/70">
          {mobileOS === MobileOS.IOS
            ? t('page.download.platform.appStore')
            : mobileOS === MobileOS.ANDROID
              ? t('page.download.platform.playStore')
              : t('page.download.platform.both')}
        </p>
      </div>

      {/* Footer */}
      <footer className="relative z-[1] mt-16 w-full max-w-lg space-y-4 text-center text-sm text-[#0F1531]/60">
        {/* Social Media & Home Link */}
        <div className="flex flex-col items-center gap-4">
          {/* Home Link */}
          <a
            href="https://www.veraconnect.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0F1531]/70 underline transition-colors hover:text-[#0F1531]">
            {t('page.download.footer.home')}
          </a>

          {/* Social Media Icons */}
          <div className="flex items-center justify-center gap-4">
            <a
              href="https://www.tiktok.com/@veraconnect"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('page.download.footer.social.tiktok')}
              className="text-[#0F1531]/70 transition-colors hover:text-[#0F1531]">
              <FaTiktok className="h-6 w-6" />
            </a>
            <a
              href="https://www.instagram.com/veraconnect"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('page.download.footer.social.instagram')}
              className="text-[#0F1531]/70 transition-colors hover:text-[#0F1531]">
              <FaInstagram className="h-6 w-6" />
            </a>
          </div>
        </div>

        {/* Copyright */}
        <p className="text-[#0F1531]/70">
          &copy; {new Date().getFullYear()} Vera Connect. {t('page.download.footer.rights')}
        </p>
      </footer>
    </div>
  );
}

export default DownloadPage;
